import { SortableContext } from "@dnd-kit/sortable";
import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { getFormValues, reduxForm } from "redux-form";
import { ActionButtonIntent, DialogComponent, Modal, Tag, TagIntent, TriggerButton, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";
import { moveItemsInArray } from "widgets/utils";

import { type IAppState } from "@/redux/reducer.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";

import { simpleDropdownFieldValidation, simpleFieldValidation } from "../../../app-utils/form.js";
import { isRawDataFileTypeNfParam } from "../../../app-utils/playbook.js";
import {
  NfParamSubTypeEnum,
  NfParamTypeEnum,
  type NfParamTypeInput,
  type NfParamValue,
  type NfPlaybookParam,
} from "../../../graphql.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { GREY_COLOR, SPECIAL_COLOR } from "../../../settings/colors-settings.js";
import { SortableItem, SortableList } from "../../core/forms/Sortable.js";
import style from "../../core/forms/styles/Sortable.module.scss";
import CheckboxField, { CheckboxReduxFormField } from "../../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import NfWorkflowParamInputField, { isHiddenNfParamType } from "../ngs/NfWorkflowParamInputField.js";

interface NfParamsConfiguratorProps {
  nfParamConfigurations: NfParamConfiguration[];
  //nfWorkflow: NfWorkflow;
  setNfParamConfigurations: (nfParamConfigurations: NfParamConfiguration[]) => void;
}

export interface NfParamConfiguration extends NfPlaybookParam {
  id?: string; // needed for the sortable context
  defaultWorkflowValue?: string;
}

const NfParamsConfigurator: FC<NfParamsConfiguratorProps> = (props) => {
  return (
    <div className="experiment-info-screen">
      <FormInputGroup>
        <FormLabel>
          {`${
            props.nfParamConfigurations.length ? props.nfParamConfigurations.length : "No "
          } workflow parameter(s) available`}
        </FormLabel>
        <FormInputWidget>
          <div className={"input-ordering"}>
            <SortableList
              onDragEnd={({ active, over }) => {
                props.setNfParamConfigurations(
                  moveItemsInArray<NfParamConfiguration, any, any>(props.nfParamConfigurations, active, over),
                );
              }}
            >
              <SortableContext items={props.nfParamConfigurations.map((nfParamConfig) => nfParamConfig.id)}>
                {props.nfParamConfigurations.map((nfParamConfig, index) => (
                  <Item
                    key={`item-${index}`}
                    index={index}
                    nfParamConfig={nfParamConfig}
                    nfParamConfigurations={props.nfParamConfigurations}
                    setNfParamConfigurations={props.setNfParamConfigurations}
                  />
                ))}
              </SortableContext>
            </SortableList>
          </div>
        </FormInputWidget>
      </FormInputGroup>
    </div>
  );
};

interface ItemProps {
  nfParamConfig: NfParamConfiguration;
  index: number;
  setNfParamConfigurations: (confs: NfParamConfiguration[]) => void;
  nfParamConfigurations: NfParamConfiguration[];
}

const Item: FC<ItemProps> = ({ nfParamConfig, index, setNfParamConfigurations, nfParamConfigurations }) => {
  const { overlayProps, state } = useOverlay();

  const onConfigChanged = (nfParamConfig: NfParamConfiguration) => {
    setNfParamConfigurations(nfParamConfigurations.map((p) => (p.id === nfParamConfig.id ? nfParamConfig : p)));
  };

  return (
    <>
      <SortableItem index={index} removeDisabled id={nfParamConfig.id}>
        <NfParamsConfigItem
          nfParamConfig={nfParamConfig}
          onVisibleStateChanged={() => {
            setNfParamConfigurations(
              nfParamConfigurations.map((p) => (p.id === nfParamConfig.id ? { ...p, hidden: !p.hidden } : p)),
            );
          }}
        />
        <>
          <NfParamsTags nfParamConfig={nfParamConfig} />
          <TriggerButton
            label="Configure Parameter"
            tooltip={"Configure how this parameter will be shown as input during the Playbook execution "}
            onPress={() => state.open()}
            icon={"edit"}
          />
        </>
      </SortableItem>
      <Modal state={state} {...overlayProps}>
        <ConfigureNfParamFormDialog
          nfParamConfig={nfParamConfig}
          closeFn={state.close}
          onConfigChanged={onConfigChanged}
        />
      </Modal>
    </>
  );
};

interface NfParamsConfigItemProps {
  nfParamConfig: NfParamConfiguration;
  onVisibleStateChanged?: (_) => void;
}

const NfParamsConfigItem: FC<NfParamsConfigItemProps> = ({ nfParamConfig, onVisibleStateChanged }) => {
  return (
    <>
      <NfParamsVisibilityToggle nfParamConfig={nfParamConfig} onVisibleStateChanged={onVisibleStateChanged} />
      <span className={style.label}>
        {nfParamConfig.label}
        <div className={style.sub_label} style={{ opacity: nfParamConfig.description ? 1 : 0.5 }}>
          {nfParamConfig.description || "No description"}
        </div>
      </span>
    </>
  );
};

const NfParamsTags: FC<{ nfParamConfig: NfParamConfiguration }> = ({ nfParamConfig }) => {
  if (isHiddenNfParamType(nfParamConfig.type)) {
    return <Tag intentColor={TagIntent.Warning}>{getParamTypeLabel(nfParamConfig.type)} (hidden)</Tag>;
  }
  return (
    <>
      {nfParamConfig.mandatory && <Tag intentColor={TagIntent.Primary}>Mandatory</Tag>}
      {nfParamConfig.type && (
        <Tag intentColor={isRawDataFileTypeNfParam(nfParamConfig) ? TagIntent.Success : TagIntent.Secondary}>
          {getParamTypeLabel(nfParamConfig.type)}
        </Tag>
      )}
    </>
  );
};

interface NfParamsVisibilityToggleProps {
  nfParamConfig: NfParamConfiguration;
  onVisibleStateChanged?: (_) => void;
}

const NfParamsVisibilityToggle: FC<NfParamsVisibilityToggleProps> = ({ nfParamConfig, onVisibleStateChanged }) => {
  const disabledTooltip = isHiddenNfParamType(nfParamConfig.type)
    ? `This parameter will be hidden because it is configured as an ${getParamTypeLabel(nfParamConfig.type)}.`
    : undefined;
  const hidden = nfParamConfig.hidden || isHiddenNfParamType(nfParamConfig.type);
  return (
    <TriggerButton
      label={"Toggle visibility"}
      tooltip={"Toggle the visibility of this parameter in the Playbook"}
      onPress={disabledTooltip ? undefined : onVisibleStateChanged}
      isDisabled={disabledTooltip}
      icon={hidden ? "visibility_off" : "visibility"}
      style={{ color: hidden ? GREY_COLOR.WHITE_2 : SPECIAL_COLOR.ACTIVE }}
    />
  );
};

interface ConfigureNfParamFormDialogProps {
  nfParamConfig: NfParamConfiguration;
  onConfigChanged: (nfParamConfig: NfParamConfiguration) => void;
  closeFn: () => void;
}

enum FieldName {
  label = "label",
  description = "description",
  mandatory = "mandatory",
  paramType = "paramType",
  defaultValue = "defaultValue",
}

interface ConfigureNfParamFormState {
  [FieldName.label]: string;
  [FieldName.description]: string;
  [FieldName.mandatory]: boolean;
  [FieldName.paramType]: NfParamTypeInput;
  [FieldName.defaultValue]: NfParamValue[];
}

const ConfigureNfParamFormDialog = reduxForm<ConfigureNfParamFormState, ConfigureNfParamFormDialogProps>({
  form: "configureNfParam",
})((props) => {
  useEffect(() => {
    props.initialize({
      [FieldName.label]: props.nfParamConfig.label,
      [FieldName.description]: props.nfParamConfig.description,
      [FieldName.mandatory]: props.nfParamConfig.mandatory || false,
      [FieldName.paramType]: props.nfParamConfig.type,
      [FieldName.defaultValue]: props.nfParamConfig.values,
    });
  }, []);

  // extract the input type so that we can display the correct input for defining default value
  const state = useSelector<IAppState, IAppState>((state) => state);
  const formValues: ConfigureNfParamFormState = getFormValues(props.form)(state);
  const configuredType: NfParamTypeInput = formValues?.[FieldName.paramType];

  const hasDefaultWorkflowValue =
    props.nfParamConfig.defaultWorkflowValue != null && props.nfParamConfig.defaultWorkflowValue != "";

  return (
    <DialogComponent
      title="Configure Your Playbook Input"
      closeFn={props.closeFn}
      mainButton={{
        label: "Done",
        onPress: props.handleSubmit(handleStoreParamConfig),
        intent: ActionButtonIntent.Primary,
      }}
      errorCallout={{ message: props.error }}
    >
      <VStack>
        <InputReduxFormField
          componentClass="input"
          name={FieldName.label}
          label={"Label"}
          component={InputField}
          validate={[simpleFieldValidation]}
        />
        <InputReduxFormField
          componentClass="input"
          name={FieldName.description}
          label={"Description"}
          component={InputField}
        />
        <CheckboxReduxFormField name={FieldName.mandatory} label={"Mandatory"} component={CheckboxField} />
        <ReactSelectReduxFormField
          name={FieldName.paramType}
          label="Input type"
          placeholder="Choose a specific input type..."
          component={ReactSelectField}
          horizontalLayout={false}
          getOptionValue={getParamTypeLabel}
          getOptionLabel={getParamTypeLabel} // required for option search function
          options={nfParamTypeOptions}
          isSearchable={false}
          validate={simpleDropdownFieldValidation}
          handleChange={() => props.change(FieldName.defaultValue, null)}
        />
        {configuredType &&
        configuredType.type !== NfParamTypeEnum.Region &&
        configuredType.type !== NfParamTypeEnum.OutputDir ? (
          <NfWorkflowParamDefaultInputField name={FieldName.defaultValue} configuredType={configuredType} />
        ) : null}
        {hasDefaultWorkflowValue && (
          <FormNote>{`If no default value is defined then this parameter will be set to: ${props.nfParamConfig.defaultWorkflowValue}`}</FormNote>
        )}
      </VStack>
    </DialogComponent>
  );
});

interface nfWorkflowParamDefaultInputFieldProps {
  name: string;
  configuredType: NfParamTypeInput;
}
const NfWorkflowParamDefaultInputField: FC<nfWorkflowParamDefaultInputFieldProps> = (props) => {
  const workflowParam: NfPlaybookParam = {
    type: props.configuredType,
    label: "Default Value",
    identifier: props.name,
    description: null,
    mandatory: false,
    values: null,
  };
  return (
    <NfWorkflowParamInputField key={workflowParam.identifier} workflowParam={workflowParam} horizontalLayout={false} />
  );
};

function handleStoreParamConfig(values, _dispatch: ReduxDispatch, props: ConfigureNfParamFormDialogProps) {
  props.onConfigChanged({
    ...props.nfParamConfig,
    label: values[FieldName.label]?.trim(),
    description: values[FieldName.description]?.trim(),
    mandatory: values[FieldName.mandatory],
    type: values[FieldName.paramType],
    values: values[FieldName.defaultValue],
  });
  props.closeFn();
}

const nfParamTypeOptions: NfParamTypeInput[] = [
  {
    type: NfParamTypeEnum.String,
    subType: null,
  },
  {
    type: NfParamTypeEnum.Number,
    subType: null,
  },
  {
    type: NfParamTypeEnum.Boolean,
    subType: null,
  },
  {
    type: NfParamTypeEnum.Region,
    subType: null,
  },
  {
    type: NfParamTypeEnum.File,
    subType: NfParamSubTypeEnum.Unknown,
  },
  {
    type: NfParamTypeEnum.File,
    subType: NfParamSubTypeEnum.File,
  },
  {
    type: NfParamTypeEnum.File,
    subType: NfParamSubTypeEnum.Folder,
  },
  {
    type: NfParamTypeEnum.File,
    subType: NfParamSubTypeEnum.Aligned,
  },
  {
    type: NfParamTypeEnum.File,
    subType: NfParamSubTypeEnum.Unaligned,
  },
  {
    type: NfParamTypeEnum.File,
    subType: NfParamSubTypeEnum.UnalignedPaired,
  },
  {
    type: NfParamTypeEnum.Genome,
    subType: NfParamSubTypeEnum.File,
  },
  {
    type: NfParamTypeEnum.Genome,
    subType: NfParamSubTypeEnum.Folder,
  },
  {
    type: NfParamTypeEnum.OutputDir,
    subType: null,
  },
];

export const getParamTypeLabel = (paramType: NfParamTypeInput) => {
  switch (paramType?.type) {
    case NfParamTypeEnum.String:
      return "Free text";
    case NfParamTypeEnum.Number:
      return "Number";
    case NfParamTypeEnum.Boolean:
      return "Boolean";
    case NfParamTypeEnum.Region:
      return "Genomic region";
    case NfParamTypeEnum.OutputDir:
      return "Output directory";
    case NfParamTypeEnum.File:
      {
        switch (paramType.subType) {
          case NfParamSubTypeEnum.Aligned:
            return "Aligned read files";
          case NfParamSubTypeEnum.Unaligned:
            return "Unaligned read files";
          case NfParamSubTypeEnum.UnalignedPaired:
            return "Paired unaligned read files";
          case NfParamSubTypeEnum.File:
            return "Any file";
          case NfParamSubTypeEnum.Folder:
            return "Any folder";
          default:
            return "Any file or folder";
        }
      }
      break;
    case NfParamTypeEnum.Genome:
      {
        switch (paramType.subType) {
          case NfParamSubTypeEnum.Folder:
            return "Genome - folder";
          default:
            return "Genome - file";
        }
      }
      break;
    default:
      return "Unknown";
  }
};

export default NfParamsConfigurator;
